import React from 'react'
import { FileText, Banknote, Weight, ClipboardList, Package, Building2, Globe, Trophy } from 'lucide-react'
import CountUp from 'react-countup'
import { formatCompactNumber } from '../utils/helpers'

const GRADIENT_MAP = {
  blue: 'from-blue-500 to-blue-600',
  emerald: 'from-emerald-500 to-emerald-600',
  violet: 'from-violet-500 to-violet-600',
  amber: 'from-amber-500 to-amber-600',
}

const BG_MAP = {
  blue: 'bg-blue-50 dark:bg-blue-950/30',
  emerald: 'bg-emerald-50 dark:bg-emerald-950/30',
  violet: 'bg-violet-50 dark:bg-violet-950/30',
  amber: 'bg-amber-50 dark:bg-amber-950/30',
}

const KPICard = ({ title, rawValue, suffix, icon: Icon, color = 'blue', loading = false }) => {
  if (loading) {
    return (
      <div className="card-sm">
        <div className="flex items-start gap-3">
          <div className="skeleton w-9 h-9 rounded-lg flex-shrink-0"></div>
          <div className="flex-1 min-w-0 space-y-2">
            <div className="skeleton h-3 w-20 rounded"></div>
            <div className="skeleton h-6 w-28 rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`card-sm-hover ${BG_MAP[color]}`}>
      <div className="flex items-start gap-3">
        <div className={`flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-br ${GRADIENT_MAP[color]} shadow-sm flex-shrink-0`}>
          <Icon className="w-4.5 h-4.5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider truncate">
            {title}
          </p>
          <p className="text-xl font-bold text-gray-900 dark:text-white mt-0.5 tabular-nums">
            <CountUp
              end={rawValue || 0}
              duration={1.2}
              separator=" "
              decimals={rawValue > 1000000 ? 0 : rawValue > 1000 ? 0 : 0}
              formattingFn={(v) => formatCompactNumber(v)}
              preserveValue
            />
            {suffix && <span className="text-xs font-medium text-gray-500 dark:text-gray-400 ml-1">{suffix}</span>}
          </p>
        </div>
      </div>
    </div>
  )
}

const TopPerformerPill = ({ label, value, icon: Icon }) => {
  if (!value) return null
  return (
    <div className="card-xs flex items-center gap-2.5 min-w-0">
      <div className="flex items-center justify-center w-7 h-7 rounded-md bg-gray-100 dark:bg-gray-800 flex-shrink-0">
        <Icon className="w-3.5 h-3.5 text-gray-600 dark:text-gray-400" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">{label}</p>
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate" title={value}>
          {value}
        </p>
      </div>
    </div>
  )
}

const KPICards = ({ data, loading = false, rankingBy = 'value' }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[...Array(4)].map((_, index) => (
          <KPICard key={index} loading={true} />
        ))}
      </div>
    )
  }

  if (!data) {
    return (
      <div className="card text-center" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
        <p className="text-sm text-gray-500 dark:text-gray-400">Sélectionnez des filtres pour afficher les KPI</p>
      </div>
    )
  }

  const kpis = [
    { title: 'Articles', rawValue: data.total_articles, icon: FileText, color: 'blue' },
    { title: 'Déclarations D10', rawValue: data.total_declarations, icon: ClipboardList, color: 'emerald' },
    { title: 'Valeur Totale', rawValue: data.total_valeur_da, suffix: 'DA', icon: Banknote, color: 'violet' },
    { title: 'Poids Total', rawValue: data.total_poids, suffix: 'Kg', icon: Weight, color: 'amber' },
  ]

  const topPerformers = [
    { label: 'Top Produit', value: data.top_produit, icon: Package },
    { label: 'Top Importateur', value: data.top_importateur, icon: Building2 },
    { label: 'Top Fournisseur', value: data.top_fournisseur, icon: Globe },
    { label: 'Top Pays', value: data.top_pays, icon: Globe },
  ].filter(t => t.value)

  return (
    <div className="space-y-4">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map((kpi, index) => (
          <KPICard key={index} {...kpi} />
        ))}
      </div>

      {/* Top Performers */}
      {topPerformers.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="w-4 h-4 text-amber-500" />
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Top Performers
            </h3>
            <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
              par {rankingBy === 'quantity' ? 'Quantité' : 'Valeur'}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {topPerformers.map((tp, i) => (
              <TopPerformerPill key={i} {...tp} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default KPICards
